<script>
    import * as Utils from '@js/modules/utils';
    import I18n from '@js/modules/translations';
    import Computation from '@js/modules/computation';

    import {poiStore} from '@js/stores/poiStore';
    import {planDateTypeLabel} from '@js/modules/plan/planPeriods';

    import PlanFilterItem from '@js/components/plan/filter/PlanFilterItem.svelte';

    const {
        allFilters,
        hasPlaces = false,
        overdueCount = 0
    } = $props();

    const initialFilters = {
        dateTypes: new Set(allFilters.dateTypes),
        rideTypes: new Set(allFilters.rideTypes),
        levels: new Set(allFilters.levels),
        distance: new Set(),
        duration: new Set()
    };

    poiStore.selectedFilters = {
        dateTypes: null,
        rideTypes: null,
        levels: null,
        distance: null,
        duration: null
    };

    const _updateFilters = Utils.debounce((selectedFilters) => {
        poiStore.selectedFilters = selectedFilters;

        poiStore.map.fetchPlanPOIs(poiStore.selectedFilters);
    }, 400);

    const onFilterChange = (filterType, filterName, filterData, checked) => {
        const selectedFilters = {...poiStore.selectedFilters};

        if (filterType === 'range') {
            if ((1 - allFilters[filterName].min / filterData[0]) < 0.08 && (1 - filterData[1] / allFilters[filterName].max) < 0.08) {
                selectedFilters[filterName] = undefined;
            } else {
                selectedFilters[filterName] = new Set(filterData);
            }
        } else if (filterType === 'switch') {
            if (filterData) {
                selectedFilters[filterName] = filterData;
            } else {
                selectedFilters[filterName] = undefined;
            }
        } else if (filterType === 'select') {
            if (!selectedFilters[filterName]) {
                selectedFilters[filterName] = new Set(initialFilters[filterName]);
            }

            if (checked) {
                selectedFilters[filterName].add(filterData.id);
            } else {
                selectedFilters[filterName].delete(filterData.id);
            }
        }

        _updateFilters(selectedFilters);
    };

    // Narrowed: a range or switch set, or a selection missing some of its choices
    const isActive = (filterName) => {
        const selected = $poiStore.selectedFilters[filterName];
        if (selected instanceof Set) {
            return !initialFilters[filterName]?.size || selected.size < initialFilters[filterName].size;
        }

        return !!selected;
    };

    const _toggleOverdue = () => {
        _updateFilters({...poiStore.selectedFilters, overdue: poiStore.selectedFilters.overdue ? undefined : true});
    };
</script>

{#if hasPlaces || $poiStore.planPois.length}
    <div class="mt-3"
         role="group"
         aria-label={I18n.t('js.ride.plan.index.filters.label')}>
        <div class="flex items-center flex-wrap justify-start gap-2">
            {#if overdueCount || $poiStore.selectedFilters.overdue}
                <!-- Full class strings: Tailwind only generates the classes it finds written whole -->
                <button type="button"
                        class={$poiStore.selectedFilters.overdue
                            ? 'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-full border cursor-pointer focus:outline-hidden focus-visible:ring-2 focus-visible:ring-yellow-300 bg-yellow-700 text-white border-yellow-700'
                            : 'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-full border cursor-pointer focus:outline-hidden focus-visible:ring-2 focus-visible:ring-yellow-300 bg-yellow-50 text-yellow-900 border-yellow-300'}
                        aria-pressed={!!$poiStore.selectedFilters.overdue}
                        onclick={_toggleOverdue}>
                    {I18n.t('js.ride.plan.index.filters.overdue')}

                    {#if !$poiStore.selectedFilters.overdue}
                        <span class="px-1.5 rounded-full bg-yellow-700 text-white text-xs font-bold">
                            {overdueCount}
                        </span>
                    {/if}
                </button>
            {/if}

            <PlanFilterItem type="switch"
                            name={I18n.t('js.ride.plan.index.filters.associated')}
                            elementId="associated"
                            active={isActive('associated')}
                            elements={I18n.t('js.ride.plan.enums.ride_associated')}
                            selectedElements={$poiStore.selectedFilters.associated || initialFilters.associated}
                            onFilterChange={onFilterChange}/>

            <PlanFilterItem type="select"
                            name={I18n.t('js.ride.plan.index.filters.date_type')}
                            elementId="dateTypes"
                            active={isActive('dateTypes')}
                            elements={allFilters.dateTypes.map((dateType) => ({
                                      id: dateType,
                                      name: planDateTypeLabel(dateType)
                                  }))}
                            selectedElements={$poiStore.selectedFilters.dateTypes || initialFilters.dateTypes}
                            onFilterChange={onFilterChange}/>

            <PlanFilterItem type="select"
                            name={I18n.t('js.ride.plan.index.filters.ride_type')}
                            elementId="rideTypes"
                            active={isActive('rideTypes')}
                            elements={allFilters.rideTypes.map((rideType) => ({
                                                  id: rideType,
                                                  name: I18n.t('js.ride.enums.ride_type')[rideType]
                                              }))}
                            selectedElements={$poiStore.selectedFilters.rideTypes || initialFilters.rideTypes}
                            onFilterChange={onFilterChange}/>

            {#if allFilters.distance.min && allFilters.distance.max}
                <PlanFilterItem type="range"
                                name={I18n.t('js.ride.plan.index.filters.distance')}
                                elementId="distance"
                                active={isActive('distance')}
                                elements={[allFilters.distance.min, allFilters.distance.max]}
                                elementFormatter={(element) => Computation.formatDistance(element, {
                            smartUnit: true,
                            kUnit: false,
                            decimalLimit: 1
                        })}
                                selectedElements={$poiStore.selectedFilters.distance || initialFilters.distance}
                                onFilterChange={onFilterChange}/>
            {/if}

            {#if allFilters.duration.min && allFilters.duration.max}
                <PlanFilterItem type="range"
                                name={I18n.t('js.ride.plan.index.filters.duration')}
                                elementId="duration"
                                active={isActive('duration')}
                                elements={[allFilters.duration.min, allFilters.duration.max]}
                                elementFormatter={(element) => Computation.formatDuration(element, {withSeconds: false})}
                                selectedElements={$poiStore.selectedFilters.duration || initialFilters.duration}
                                onFilterChange={onFilterChange}/>
            {/if}

            {#if allFilters.levels?.length}
                <PlanFilterItem type="select"
                                name={I18n.t('js.ride.plan.index.filters.level')}
                                elementId="levels"
                                active={isActive('levels')}
                                elements={allFilters.levels.map((level) => ({
                                                  id: level,
                                                  name: I18n.t('js.ride.enums.level')[level]
                                              }))}
                                selectedElements={$poiStore.selectedFilters.levels || initialFilters.levels}
                                onFilterChange={onFilterChange}/>
            {/if}
        </div>
    </div>
{/if}
